// src/api/transactions-api.js
import { gql } from '@apollo/client';
import client from './apollo-client';

// GraphQL Queries
export const GET_TRANSACTIONS = gql`
  query GetTransactions {
    transactions {
      id
      date
      gain
      companyCommission
      status
      type
      avantGain
      apresGain
      labelId
      productId
      pharmacyId
      userId
      paymentRequestId
      createdAt
      updatedAt
      user {
        id
        email
        firstName
        lastName
        userType
        tel
      }
      product {
        id
        name
        price
      }
      pharmacy {
        id
        namePharmacy
        telPharmacy
        region {
          id
          nomRegion
        }
        ville {
          id
          nomVilleVillage
        }
      }
      label {
        id
        code
        date
        status
        promotion {
          id
          name
        }
      }
      paymentRequest {
        id
        amount
        status
        dateDemande
      }
    }
  }
`;

export const GET_TRANSACTION = gql`
  query GetTransaction($id: ID!) {
    transaction(id: $id) {
      id
      date
      gain
      companyCommission
      status
      type
      avantGain
      apresGain
      labelId
      productId
      pharmacyId
      userId
      paymentRequestId
      createdAt
      updatedAt
      user {
        id
        email
        firstName
        lastName
        userType
        tel
      }
      product {
        id
        name
        price
      }
      pharmacy {
        id
        namePharmacy
        telPharmacy
        region {
          id
          nomRegion
        }
        ville {
          id
          nomVilleVillage
        }
      }
      label {
        id
        code
        date
        status
        promotion {
          id
          name
        }
      }
      paymentRequest {
        id
        amount
        status
        dateDemande
      }
    }
  }
`;

export const GET_TRANSACTIONS_BY_USER = gql`
  query GetTransactionsByUser($userId: ID!) {
    transactionsByUser(userId: $userId) {
      id
      date
      gain
      companyCommission
      status
      type
      avantGain
      apresGain
      labelId
      productId
      pharmacyId
      userId
      paymentRequestId
      createdAt
      updatedAt
      user {
        id
        email
        firstName
        lastName
        userType
        tel
      }
      product {
        id
        name
        price
      }
      pharmacy {
        id
        namePharmacy
        telPharmacy
        region {
          id
          nomRegion
        }
        ville {
          id
          nomVilleVillage
        }
      }
      label {
        id
        code
        date
        status
        promotion {
          id
          name
        }
      }
      paymentRequest {
        id
        amount
        status
        dateDemande
      }
    }
  }
`;

export const GET_TRANSACTIONS_PAGINATED = gql`
  query GetTransactionsPaginated(
    $page: Int
    $limit: Int
    $status: String
    $type: String
    $search: String
    $dateFrom: String
    $dateTo: String
    $sortBy: String
    $sortOrder: String
    $productName: String
    $laboratoryId: String
  ) {
    transactionsPaginated(
      page: $page
      limit: $limit
      status: $status
      type: $type
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
      sortBy: $sortBy
      sortOrder: $sortOrder
      productName: $productName
      laboratoryId: $laboratoryId
    ) {
      transactions {
        id
        date
        gain
        companyCommission
        status
        type
        avantGain
        apresGain
        labelId
        productId
        pharmacyId
        userId
        paymentRequestId
        createdAt
        updatedAt
        user {
          id
          email
          firstName
          lastName
          userType
          tel
        }
        product {
          id
          name
          price
        }
        pharmacy {
          id
          namePharmacy
          telPharmacy
          region {
            id
            nomRegion
          }
          ville {
            id
            nomVilleVillage
          }
        }
        label {
          id
          code
          date
          status
          userId
          promotion {
            id
            name
          }
        }
        paymentRequest {
          id
          amount
          status
          dateDemande
        }
      }
      total
      page
      totalPages
    }
  }
`;

export const GET_TRANSACTIONS_BY_STATUS = gql`
  query GetTransactionsByStatus($status: TransactionStatus!) {
    transactionsByStatus(status: $status) {
      id
      date
      gain
      status
      type
      user {
        firstName
        lastName
      }
    }
  }
`;

export const GET_TRANSACTIONS_BY_TYPE = gql`
  query GetTransactionsByType($type: TransactionType!) {
    transactionsByType(type: $type) {
      id
      date
      gain
      status
      type
      user {
        firstName
        lastName
      }
    }
  }
`;

export const GET_TRANSACTION_STATISTICS = gql`
  query GetTransactionStatistics($dateFrom: String, $dateTo: String) {
    transactionStatistics(dateFrom: $dateFrom, dateTo: $dateTo) {
      total
      pending
      validated
      rejected
      scans
      withdrawals
      totalGains
      totalCommissions
      thisMonth
      thisMonthGains
    }
  }
`;

export const GET_DUPLICATE_TRANSACTIONS = gql`
  query GetDuplicateTransactions {
    duplicateTransactions {
      id
      date
      gain
      companyCommission
      status
      type
      user {
        id
        firstName
        lastName
        email
      }
      product {
        id
        name
        price
      }
      label {
        id
        code
        status
      }
      pharmacy {
        id
        namePharmacy
      }
    }
  }
`;

// GraphQL Mutations
export const UPDATE_TRANSACTION_STATUS = gql`
  mutation UpdateTransactionStatus($id: ID!, $status: TransactionStatus!) {
    updateTransactionStatus(id: $id, status: $status) {
      id
      status
    }
  }
`;

export const DELETE_DUPLICATE_TRANSACTION = gql`
  mutation DeleteDuplicateTransaction($transactionId: ID!) {
    deleteDuplicateTransaction(transactionId: $transactionId) {
      success
      message
      transactionId
      userAmountSubtracted
      adminAmountSubtracted
    }
  }
`;

// Transactions API functions
export const transactionsApi = {
  // Export queries for direct use in components
  GET_TRANSACTIONS,
  GET_TRANSACTIONS_PAGINATED,
  GET_TRANSACTION,
  GET_TRANSACTIONS_BY_USER,
  GET_TRANSACTIONS_BY_STATUS,
  GET_TRANSACTIONS_BY_TYPE,
  GET_TRANSACTION_STATISTICS,
  GET_DUPLICATE_TRANSACTIONS,
  UPDATE_TRANSACTION_STATUS,
  DELETE_DUPLICATE_TRANSACTION,

  /**
   * Get all transactions with full details
   */
  getTransactions: async () => {
    try {
      const { data } = await client.query({
        query: GET_TRANSACTIONS,
        fetchPolicy: 'network-only'
      });
      return data.transactions;
    } catch (error) {
      console.error('Get transactions error:', error);
      throw error;
    }
  },

  /**
   * Get a single transaction by ID
   */
  getTransaction: async (id) => {
    try {
      const { data } = await client.query({
        query: GET_TRANSACTION,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return data.transaction;
    } catch (error) {
      console.error(`Get transaction ${id} error:`, error);
      throw error;
    }
  },

  /**
   * Get all transactions for a specific user
   */
  getTransactionsByUser: async (userId) => {
    try {
      const { data } = await client.query({
        query: GET_TRANSACTIONS_BY_USER,
        variables: { userId },
        fetchPolicy: 'network-only'
      });
      return data.transactionsByUser;
    } catch (error) {
      console.error(`Get transactions by user ${userId} error:`, error);
      throw error;
    }
  },

  /**
   * Get transactions filtered by status
   */
  getTransactionsByStatus: async (status) => {
    try {
      const { data } = await client.query({
        query: GET_TRANSACTIONS_BY_STATUS,
        variables: { status },
        fetchPolicy: 'network-only'
      });
      return data.transactionsByStatus;
    } catch (error) {
      console.error(`Get transactions by status ${status} error:`, error);
      throw error;
    }
  },

  /**
   * Get transactions filtered by type (SCAN/WITHDRAWAL)
   */
  getTransactionsByType: async (type) => {
    try {
      const { data } = await client.query({
        query: GET_TRANSACTIONS_BY_TYPE,
        variables: { type },
        fetchPolicy: 'network-only'
      });
      return data.transactionsByType;
    } catch (error) {
      console.error(`Get transactions by type ${type} error:`, error);
      throw error;
    }
  },

  /**
   * Get transaction statistics and analytics
   */
  getTransactionStatistics: async () => {
    try {
      const { data } = await client.query({
        query: GET_TRANSACTION_STATISTICS,
        fetchPolicy: 'network-only'
      });
      return data.transactionStatistics;
    } catch (error) {
      console.error('Get transaction statistics error:', error);
      throw error;
    }
  },

  /**
   * Get all duplicate transactions (transactions with same QR code)
   */
  getDuplicateTransactions: async () => {
    try {
      const { data } = await client.query({
        query: GET_DUPLICATE_TRANSACTIONS,
        fetchPolicy: 'network-only'
      });
      return data.duplicateTransactions;
    } catch (error) {
      console.error('Get duplicate transactions error:', error);
      throw error;
    }
  },

  /**
   * Update transaction status
   */
  updateTransactionStatus: async (id, status) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_TRANSACTION_STATUS,
        variables: { id, status },
        refetchQueries: [
          { query: GET_TRANSACTIONS },
          { query: GET_TRANSACTION, variables: { id } }
        ]
      });
      return data.updateTransactionStatus;
    } catch (error) {
      console.error(`Update transaction ${id} status error:`, error);
      throw error;
    }
  },

  /**
   * Delete a duplicate transaction and adjust commissions
   * Subtracts commission from both user and admin (Mazid)
   * Resets QR label to unused status
   */
  deleteDuplicateTransaction: async (transactionId) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_DUPLICATE_TRANSACTION,
        variables: { transactionId },
        refetchQueries: [
          { query: GET_TRANSACTIONS },
          { query: GET_DUPLICATE_TRANSACTIONS },
          // Note: You may want to add GET_TRANSACTIONS_BY_USER if you know the userId
        ]
      });
      return data.deleteDuplicateTransaction;
    } catch (error) {
      console.error(`Delete duplicate transaction ${transactionId} error:`, error);
      throw error;
    }
  }
};